import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Box, Layers } from "lucide-react";
import { api, type Assembly, type Model } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CollectionTabs, LibraryFooter, LibraryHeader, LoadingGrid, matchesSearch, ModelSections, SearchBox } from "../components/Library";

export default function Home() {
  const { user } = useAuth();
  const [models, setModels] = useState<Model[] | null>(null);
  const [assemblies, setAssemblies] = useState<Assembly[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .listModels()
      .then(({ models }) => setModels(models))
      .catch((e) => setError(e.message));
    // Assemblies are a bonus row — a failure here shouldn't blank the page.
    api
      .listAssemblies()
      .then(({ assemblies }) => setAssemblies(assemblies))
      .catch(() => setAssemblies([]));
  }, []);

  const visibleAssemblies = useMemo(() => {
    if (!assemblies) return null;
    return assemblies.filter((a) => matchesSearch(a, search));
  }, [assemblies, search]);

  // Embryology has its own page; this library is everything else.
  const filtered = useMemo(() => {
    if (!models) return null;
    return models.filter((m) => m.collection !== "embryology" && matchesSearch(m, search));
  }, [models, search]);

  return (
    <div className="min-h-screen bg-[#f7f8f5]">
      <LibraryHeader />
      <main className="mx-auto max-w-7xl px-5 pb-12 sm:px-8">
        <div className="flex flex-col justify-between gap-4 py-8 sm:flex-row sm:items-end sm:py-10">
          <div className="w-full sm:w-auto">
            <CollectionTabs />
            <h1 className="text-2xl font-semibold tracking-tight text-slate-800 sm:text-3xl">Anatomy library</h1>
            <p className="mt-2 text-sm text-slate-500">Explore 3D models and interactive assemblies, region by region.</p>
          </div>
          <SearchBox value={search} onChange={setSearch} label="Search anatomy library" />
        </div>
        {error && <div role="alert" className="card mb-6 border-red-200 bg-red-50 p-4 text-sm text-red-700">Couldn't load models: {error}</div>}
        {visibleAssemblies && visibleAssemblies.length > 0 && <section className="mb-10">
          <div className="mb-4 flex items-center gap-3"><h3 className="section-title">Interactive assemblies</h3><span className="chip">{visibleAssemblies.length}</span><div className="h-px flex-1 bg-slate-200/70" /></div>
          <div className="grid gap-5 md:grid-cols-2">{visibleAssemblies.map((assembly) => {
            const preview = api.assemblyPreviewUrl(assembly);
            return <Link key={assembly.id} to={`/assembly/${assembly.id}`} className="card group flex flex-col overflow-hidden transition-colors hover:border-brand-300 sm:flex-row">
              <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-[#eef2ef] sm:aspect-auto sm:w-2/5 sm:shrink-0">
                {preview ? <img src={preview} alt={`${assembly.name} preview`} className="h-full w-full object-contain" /> : <Layers size={40} strokeWidth={1} className="text-brand-400" />}
                <span className="absolute left-3 top-3 rounded-full border border-white/80 bg-white/90 px-2 py-1 text-[9px] font-medium text-brand-800">{assembly.part_count ?? 0} parts</span>
              </div>
              <div className="flex flex-1 flex-col p-6"><p className="eyebrow text-brand-700">Explore + practise</p><h4 className="mt-3 text-lg font-semibold leading-snug tracking-tight text-slate-800 group-hover:text-brand-700">{assembly.name}</h4>{assembly.description && <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-slate-500">{assembly.description}</p>}<span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-brand-700">Open assembly<ArrowUpRight size={15} /></span></div>
            </Link>;
          })}</div>
        </section>}
        {filtered && filtered.length > 0 && <ModelSections collection="anatomy" models={filtered} />}
        {models === null && !error && <LoadingGrid />}
        {models && filtered?.length === 0 && visibleAssemblies?.length === 0 && <div className="card py-14 text-center"><Box size={28} className="mx-auto mb-4 text-brand-400" /><h3 className="font-semibold text-slate-800">{search ? "No matching models" : "Your library is taking shape"}</h3><p className="mt-2 text-sm text-slate-500">{search ? "Try another organ name or clear your search." : "Models will appear here when your teacher adds them."}</p>{search && <button onClick={() => setSearch("")} className="btn-secondary mt-5">Clear search</button>}{!search && user?.role === "admin" && <Link to="/admin" className="btn-primary mt-5">Add your first scan</Link>}</div>}
        <LibraryFooter />
      </main>
    </div>
  );
}
